/*
 * <mix-match-builder> — Mix&Match build-your-own pack.
 *
 * Tier math (per line):
 *   units      = sticks (3oz) or bags (1oz), summed across flavors
 *   tier       = last config tier whose `min` <= units
 *   one-time   = anchor price − tier.one_time %   (applied at checkout by Shopify automatic discounts)
 *   subscribe  = anchor price − tier.subscribe %  (selling plan tier.plans[frequency], priced by Loop)
 *
 * One-time adds plain line items; subscribe adds the same line items, each with the same selling plan.
 * Cart requests go through a fetch taken from a hidden same-origin iframe, because Moon Bundles
 * wraps window.fetch sitewide and rewrites /cart/add.js payloads.
 */
if (!customElements.get('mix-match-builder')) {
  customElements.define(
    'mix-match-builder',
    class MixMatchBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.config = this.normalizeConfig(this.readJson('[data-mm-config]') || {});
        this.flavorData = this.readJson('[data-mm-flavors]') || {};
        this.settings = this.readJson('[data-mm-settings]') || {};
        this.lines = this.config.lines || {};

        this.state = {
          line: this.lines[this.dataset.defaultLine] ? this.dataset.defaultLine : Object.keys(this.lines)[0],
          purchase: 'subscribe',
          frequency: this.config.default_frequency || (this.config.frequencies?.[0]?.key ?? ''),
          counts: {},
        };
        Object.keys(this.lines).forEach((key) => (this.state.counts[key] = {}));

        this.el = {
          pill: this.querySelector('[data-mm-pill]'),
          summaryBadge: this.querySelector('[data-mm-summary-badge]'),
          total: this.querySelector('[data-mm-total]'),
          totalMeta: this.querySelector('[data-mm-total-meta]'),
          nudge: this.querySelector('[data-mm-nudge]'),
          progress: this.querySelector('[data-mm-progress]'),
          subscribe: this.querySelector('[data-mm-option="subscribe"]'),
          oneTime: this.querySelector('[data-mm-option="one_time"]'),
          frequency: this.querySelector('[data-mm-frequency]'),
          subscribeNote: this.querySelector('[data-mm-subscribe-note]'),
          oneTimeNote: this.querySelector('[data-mm-one-time-note]'),
          swap: this.querySelector('[data-mm-swap]'),
          add: this.querySelector('[data-mm-add]'),
          error: this.querySelector('[data-mm-error]'),
        };
        this.subscribeNoteDefault = this.el.subscribeNote.textContent;

        this.buildFrequencyOptions();
        this.bindEvents();
        this.render();
      }

      /*
       * Maps the shop metafield `mix_match.config` (client schema) to the internal shape:
       *   line:  min_units → min_one_time, subscribe_min_units → min_subscribe, sticks_per_unit (default 1)
       *   tier:  one_time_pct → one_time, subscribe_pct → subscribe,
       *          plans = line.selling_plans[tier.plan_key]  ({ '1_week': id, '1_month': id, ... })
       */
      normalizeConfig(raw) {
        const frequencies = raw.frequencies || [
          { key: '1_week', label: 'Deliver Every Week' },
          { key: '1_month', label: 'Deliver Every Month' },
          { key: '2_months', label: 'Deliver Every 2 Months' },
          { key: '3_months', label: 'Deliver Every 3 Months' },
        ];
        const lines = {};
        Object.entries(raw.lines || {}).forEach(([key, line]) => {
          const plansTable = line.selling_plans || {};
          lines[key] = {
            unit: line.unit,
            sticks_per_unit: line.sticks_per_unit || 1,
            min_one_time: line.min_units ?? line.min_one_time ?? 1,
            min_subscribe: line.subscribe_min_units ?? line.min_subscribe ?? line.min_units ?? 1,
            tiers: (line.tiers || [])
              .map((tier) => ({
                min: tier.min,
                max: tier.max ?? null,
                one_time: tier.one_time_pct ?? tier.one_time ?? 0,
                subscribe: tier.subscribe_pct !== undefined ? tier.subscribe_pct : tier.subscribe ?? null,
                plans: tier.plan_key ? plansTable[tier.plan_key] || null : tier.plans || null,
              }))
              .sort((a, b) => a.min - b.min),
          };
        });
        return {
          version: raw.version,
          frequencies,
          default_frequency: raw.default_frequency || (frequencies.some((f) => f.key === '1_month') ? '1_month' : frequencies[0]?.key),
          lines,
        };
      }

      readJson(selector) {
        const node = this.querySelector(selector);
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error(`mix-match-builder: invalid JSON in ${selector}`, error);
          return null;
        }
      }

      buildFrequencyOptions() {
        (this.config.frequencies || []).forEach((frequency) => {
          const option = document.createElement('option');
          option.value = frequency.key;
          option.textContent = frequency.label;
          option.selected = frequency.key === this.state.frequency;
          this.el.frequency.appendChild(option);
        });
      }

      bindEvents() {
        this.querySelectorAll('[data-mm-line-button]').forEach((button) => {
          button.addEventListener('click', () => {
            this.state.line = button.dataset.mmLineButton;
            this.render();
          });
        });

        this.querySelectorAll('[data-mm-flavor]').forEach((card) => {
          card.querySelectorAll('[data-mm-step]').forEach((button) => {
            button.addEventListener('click', () => this.step(card, parseInt(button.dataset.mmStep, 10)));
          });
        });

        [this.el.subscribe, this.el.oneTime].forEach((option) => {
          const select = () => {
            if (option.getAttribute('aria-disabled') === 'true') return;
            this.state.purchase = option.dataset.mmOption;
            this.render();
          };
          option.addEventListener('click', (event) => {
            if (event.target.closest('select')) return;
            select();
          });
          option.addEventListener('keydown', (event) => {
            if (event.target !== option) return;
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              select();
            }
          });
        });

        this.el.frequency.addEventListener('change', () => {
          this.state.frequency = this.el.frequency.value;
          this.state.purchase = 'subscribe';
          this.render();
        });

        this.el.add.addEventListener('click', this.onAdd.bind(this));
      }

      step(card, delta) {
        const counts = this.state.counts[this.state.line];
        const id = card.dataset.variantId;
        const next = Math.min(99, Math.max(0, (counts[id] || 0) + delta));
        counts[id] = next;
        card.querySelector('[data-mm-count]').textContent = String(next);
        this.render();
      }

      /* ---------- tier math ---------- */

      get line() {
        return this.lines[this.state.line];
      }

      flavorsFor(lineKey) {
        return this.flavorData[lineKey] || [];
      }

      tierIndexFor(units) {
        const tiers = this.line.tiers || [];
        let index = -1;
        tiers.forEach((tier, i) => {
          if (units >= tier.min) index = i;
        });
        return index;
      }

      subscribeBlocked(units) {
        const line = this.line;
        return line.min_subscribe > line.min_one_time && units < line.min_subscribe;
      }

      planIdFor(tier) {
        return tier && tier.plans ? tier.plans[this.state.frequency] ?? null : null;
      }

      compute() {
        const lineKey = this.state.line;
        const line = this.line;
        const counts = this.state.counts[lineKey];
        const flavors = this.flavorsFor(lineKey);
        const sticksPerUnit = line.sticks_per_unit || 1;
        const tiers = line.tiers || [];

        const units = flavors.reduce((sum, flavor) => sum + (counts[flavor.variant_id] || 0), 0);
        const tierIndex = this.tierIndexFor(units);
        const tier = tiers[Math.max(tierIndex, 0)];
        const anchor = flavors.find((flavor) => flavor.available)?.price ?? flavors[0]?.price ?? 0;

        const oneTimePct = tier?.one_time || 0;
        const subscribePct = tier?.subscribe ?? null;
        const planId = this.planIdFor(tier);

        const subscribeUnitPrice = (flavor) => {
          const planPrice = planId != null ? flavor.plan_prices?.[String(planId)] : undefined;
          if (planPrice != null) return planPrice;
          return Math.round(flavor.price * (1 - (subscribePct || 0) / 100));
        };

        let subtotal = 0;
        let subscribeTotal = 0;
        flavors.forEach((flavor) => {
          const count = counts[flavor.variant_id] || 0;
          subtotal += count * flavor.price;
          subscribeTotal += count * subscribeUnitPrice(flavor);
        });
        const oneTimeTotal = Math.round(subtotal * (1 - oneTimePct / 100));

        // Per-unit prices for display, also when nothing is selected yet.
        const anchorFlavor = { price: anchor, plan_prices: flavors[0]?.plan_prices };
        const oneTimeUnit = units ? oneTimeTotal / units : anchor * (1 - oneTimePct / 100);
        const subscribeUnit = units ? subscribeTotal / units : subscribeUnitPrice(anchorFlavor);

        const blocked = this.subscribeBlocked(units);
        const subscribeAvailable = !blocked && subscribePct != null && planId != null;

        let purchase = this.state.purchase;
        if (purchase === 'subscribe' && !subscribeAvailable) purchase = 'one_time';

        return {
          line,
          lineKey,
          units,
          sticks: units * sticksPerUnit,
          sticksPerUnit,
          tiers,
          tierIndex,
          tier,
          anchor,
          oneTimePct,
          subscribePct,
          planId,
          blocked,
          subscribeAvailable,
          purchase,
          oneTimeTotal,
          subscribeTotal,
          oneTimeUnit,
          subscribeUnit,
          meetsMinimum: units >= line.min_one_time,
        };
      }

      pctFor(tier, purchase) {
        return purchase === 'subscribe' ? tier.subscribe : tier.one_time;
      }

      /* ---------- rendering ---------- */

      render() {
        const c = this.compute();
        const isBag = c.line.unit === 'bag';
        const pct = this.pctFor(c.tier, c.purchase) || 0;
        const total = c.purchase === 'subscribe' ? c.subscribeTotal : c.oneTimeTotal;
        const unitPrice = c.purchase === 'subscribe' ? c.subscribeUnit : c.oneTimeUnit;
        const perStick = unitPrice / c.sticksPerUnit;

        this.renderLineSwitch();

        // Summary
        this.el.pill.textContent = isBag
          ? `${this.plural(c.units, 'bag')} · ${this.plural(c.sticks, 'stick')}`
          : `${c.units} Stick(s) Selected`;
        this.toggleBadge(this.el.summaryBadge, c.meetsMinimum ? pct : 0);
        this.el.total.textContent = this.money(total, true);
        this.el.totalMeta.textContent = isBag
          ? `${this.money(perStick)} / stick · ${this.money(unitPrice)} / bag`
          : `${this.money(perStick)} / stick · ${this.plural(c.units, 'stick')}`;

        this.el.nudge.textContent = this.nudgeText(c, isBag);
        this.renderProgress(c, isBag);
        this.renderOptions(c, isBag);

        // Add to cart
        const canAdd = c.meetsMinimum && (c.purchase === 'one_time' || c.subscribeAvailable);
        this.el.add.disabled = !canAdd;
        this.el.add.setAttribute('aria-disabled', String(!canAdd));
      }

      renderLineSwitch() {
        const lineKey = this.state.line;
        this.querySelectorAll('[data-mm-line-button]').forEach((button) => {
          const key = button.dataset.mmLineButton;
          button.setAttribute('aria-checked', String(key === lineKey));
          const meta = button.querySelector(`[data-mm-line-meta="${key}"]`);
          const line = this.lines[key];
          const anchor = this.flavorsFor(key)[0]?.price;
          if (meta && line && anchor != null) {
            const perStick = this.money(anchor / (line.sticks_per_unit || 1));
            meta.textContent =
              line.unit === 'bag'
                ? `Bags of ${line.sticks_per_unit} · ${perStick} / stick`
                : `Single sticks · ${perStick} / stick`;
          }
        });
        this.querySelectorAll('[data-mm-grid]').forEach((grid) => (grid.hidden = grid.dataset.mmGrid !== lineKey));
        this.querySelectorAll('[data-mm-line-only]').forEach((node) => (node.hidden = node.dataset.mmLineOnly !== lineKey));

        this.querySelectorAll(`[data-mm-grid="${lineKey}"] [data-mm-flavor]`).forEach((card) => {
          const count = this.state.counts[lineKey][card.dataset.variantId] || 0;
          const minus = card.querySelector('[data-mm-step="-1"]');
          minus.disabled = count <= 0;
        });
      }

      nudgeText(c, isBag) {
        const unitWord = (n) => (isBag ? `${this.plural(n, 'bag')} (${this.plural(n * c.sticksPerUnit, 'stick')})` : this.plural(n, 'stick'));

        if (!c.meetsMinimum) {
          const missing = c.line.min_one_time - c.units;
          if (isBag) return `Add ${unitWord(missing)} to get started`;
          return `Add ${missing} more ${missing === 1 ? 'stick' : 'sticks'} to reach the ${c.line.min_one_time}-stick minimum`;
        }

        const currentPct = this.pctFor(c.tier, c.purchase) || 0;
        const next = c.tiers.slice(c.tierIndex + 1).find((tier) => (this.pctFor(tier, c.purchase) || 0) > currentPct);
        if (next) {
          const missing = next.min - c.units;
          const amount = isBag ? unitWord(missing) : `${missing} more ${missing === 1 ? 'stick' : 'sticks'}`;
          return `Add ${amount} to unlock ${this.pctFor(next, c.purchase)}% off`;
        }
        return currentPct > 0 ? `You've unlocked the best price — ${currentPct}% off` : '';
      }

      renderProgress(c, isBag) {
        const fragment = document.createDocumentFragment();
        c.tiers.forEach((tier, i) => {
          const start = i === 0 ? 0 : c.tiers[i - 1].min;
          const span = tier.min - start || 1;
          const fill = Math.min(1, Math.max(0, (c.units - start) / span));
          const isLast = i === c.tiers.length - 1;
          const pct = this.pctFor(tier, c.purchase);

          const segment = document.createElement('div');
          segment.className = 'mm-progress__segment';
          if (c.units >= tier.min) segment.classList.add('is-reached');

          const track = document.createElement('div');
          track.className = 'mm-progress__track';
          const bar = document.createElement('div');
          bar.className = 'mm-progress__fill';
          bar.style.width = `${fill * 100}%`;
          track.appendChild(bar);
          if (fill > 0 && fill < 1) {
            const knob = document.createElement('span');
            knob.className = 'mm-progress__knob';
            knob.style.left = `${fill * 100}%`;
            track.appendChild(knob);
          }
          segment.appendChild(track);

          const sticks = tier.min * c.sticksPerUnit;
          segment.appendChild(this.label('mm-progress__label', `${sticks}${isLast ? '+' : ''} sticks`));
          if (isBag) {
            segment.appendChild(
              this.label('mm-progress__sub', isLast ? `${tier.min}+ bags` : this.plural(tier.min, 'bag'))
            );
          }
          segment.appendChild(this.label('mm-progress__sub', pct == null ? '—' : `${pct}%`));
          fragment.appendChild(segment);
        });
        this.el.progress.replaceChildren(fragment);
        this.el.progress.setAttribute('role', 'img');
        this.el.progress.setAttribute(
          'aria-label',
          `${this.plural(c.sticks, 'stick')} selected${c.tierIndex >= 0 ? `, ${this.pctFor(c.tier, c.purchase) || 0}% off` : ''}`
        );
      }

      renderOptions(c, isBag) {
        const sub = this.el.subscribe;
        const one = this.el.oneTime;
        const subscribeDisabled = !c.subscribeAvailable;

        sub.setAttribute('aria-checked', String(c.purchase === 'subscribe'));
        one.setAttribute('aria-checked', String(c.purchase === 'one_time'));
        sub.setAttribute('aria-disabled', String(subscribeDisabled));
        sub.classList.toggle('is-disabled', subscribeDisabled);
        sub.tabIndex = subscribeDisabled ? -1 : 0;
        this.el.frequency.disabled = c.blocked;

        // Subscribe row
        const subBadge = sub.querySelector('[data-mm-option-badge]');
        const subPrice = sub.querySelector('[data-mm-option-price]');
        if (c.blocked || c.subscribePct == null) {
          this.toggleBadge(subBadge, 0);
          subPrice.textContent = '—';
          const min = c.line.min_subscribe;
          this.el.subscribeNote.textContent = isBag
            ? `Subscriptions start at ${this.plural(min, 'bag')} (${this.plural(min * c.sticksPerUnit, 'stick')})`
            : `Subscriptions start at ${this.plural(min, 'stick')}`;
        } else {
          this.toggleBadge(subBadge, c.subscribePct);
          subPrice.textContent = `${this.money(c.subscribeUnit / c.sticksPerUnit)} / stick`;
          this.el.subscribeNote.textContent =
            c.planId == null ? 'This delivery option is not available yet' : this.subscribeNoteDefault;
        }

        // One-time row
        const oneBadge = one.querySelector('[data-mm-option-badge]');
        const onePrice = one.querySelector('[data-mm-option-price]');
        this.toggleBadge(oneBadge, c.oneTimePct);
        onePrice.textContent = `${this.money(c.oneTimeUnit / c.sticksPerUnit)} / stick`;
        if (isBag) {
          this.el.oneTimeNote.textContent = `${this.money(c.oneTimeUnit)} / bag · ships once`;
        } else {
          this.el.oneTimeNote.textContent =
            c.oneTimePct > 0 && c.meetsMinimum
              ? `Ships once · ${c.oneTimePct}% off at ${this.plural(c.units, 'stick')}`
              : 'Ships once';
        }

        this.el.swap.hidden = c.blocked;
      }

      toggleBadge(badge, pct) {
        badge.hidden = !pct;
        if (pct) badge.textContent = `Save ${pct}%`;
      }

      label(className, text) {
        const node = document.createElement('span');
        node.className = className;
        node.textContent = text;
        return node;
      }

      plural(n, word) {
        return `${n} ${n === 1 ? word : `${word}s`}`;
      }

      money(cents, withCurrency = false) {
        const format =
          (withCurrency ? this.settings.money_with_currency_format : this.settings.money_format) || '${{amount}}';
        // Floor cents so a displayed per-unit price never overstates what Shopify's per-line
        // discount rounding will charge at checkout. Totals reaching this fn are already
        // integer cents from compute(), so flooring an int is a no-op there.
        const value = Math.floor(cents) / 100;
        const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
          const [whole, fraction] = number.toFixed(decimals).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? `${grouped}${decimal}${fraction}` : grouped;
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return withDelimiters(value, 0);
            case 'amount_with_comma_separator':
              return withDelimiters(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withDelimiters(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return withDelimiters(value, 2, "'", '.');
            default:
              return withDelimiters(value, 2);
          }
        });
      }

      /* ---------- add to cart ---------- */

      getCleanFetch() {
        if (this.cleanFetch) return this.cleanFetch;
        try {
          const iframe = document.createElement('iframe');
          iframe.setAttribute('aria-hidden', 'true');
          iframe.tabIndex = -1;
          iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
          document.body.appendChild(iframe);
          const nativeFetch = iframe.contentWindow && iframe.contentWindow.fetch;
          if (typeof nativeFetch === 'function') {
            this.cleanFetch = nativeFetch.bind(iframe.contentWindow);
            return this.cleanFetch;
          }
        } catch (error) {
          console.warn('mix-match-builder: could not create a clean fetch, falling back to window.fetch', error);
        }
        this.cleanFetch = window.fetch.bind(window);
        return this.cleanFetch;
      }

      generateBundleId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
        return `mm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      showError(message) {
        this.el.error.textContent = message || '';
        this.el.error.hidden = !message;
      }

      async onAdd() {
        const c = this.compute();
        const canAdd = c.meetsMinimum && (c.purchase === 'one_time' || c.subscribeAvailable);
        if (!canAdd || this.el.add.classList.contains('is-loading')) return;

        const counts = this.state.counts[c.lineKey];
        const bundleId = this.generateBundleId();
        const items = this.flavorsFor(c.lineKey)
          .filter((flavor) => (counts[flavor.variant_id] || 0) > 0)
          .map((flavor) => {
            const item = {
              id: flavor.variant_id,
              quantity: counts[flavor.variant_id],
              properties: { _bundle_id: bundleId, _mix_match: c.lineKey },
            };
            if (c.purchase === 'subscribe') item.selling_plan = c.planId;
            return item;
          });
        if (!items.length) return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.el.add);
        }

        this.showError('');
        this.el.add.classList.add('is-loading');
        this.el.add.setAttribute('aria-busy', 'true');

        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const url = new URL(`${root}cart/add.js`, window.location.origin).toString();
          const response = await this.getCleanFetch()(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          this.debug('add.js response', response.status, data);

          if (!response.ok || data.status) {
            this.showError(data.description || data.message || 'Could not add this pack to the cart. Please try again.');
            return;
          }

          // Moon Bundles' sitewide app-embed writes moonbundle_* keys into cart.attributes
          // on every cart mutation it observes. Wipe them via the clean iframe fetch so
          // they don't reach checkout. Best-effort: never blocks the drawer/redirect flow.
          await this.cleanupMoonBundleAttributes(root);

          this.dispatchEvent(
            new CustomEvent('mix-match:added', { bubbles: true, detail: { line: c.lineKey, purchase: c.purchase, items } })
          );

          this.resetLine(c.lineKey);

          const modal = this.closest('modal-dialog');

          // The store's visible cart is Moon Bundles' own drawer. It only learns about adds through its
          // window.fetch hook, which we bypass on purpose — so ask it to reload the cart and open.
          const moonDrawer = document.querySelector('moonbundle-cart-drawer-block');
          if (moonDrawer && typeof moonDrawer.open === 'function') {
            this.debug('opening Moon Bundles cart drawer');
            if (modal && modal.hasAttribute('open')) modal.hide();
            moonDrawer.open(true);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mix-match-builder' });
            }
            return;
          }

          if (!cart || !body.sections || typeof cart.renderContents !== 'function') {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          // Re-read the sections after the add so the drawer never shows a stale (empty) cart.
          const sections = (await this.fetchSections(root, body.sections)) || data.sections;
          const state =
            cart.tagName.toLowerCase() === 'cart-notification' ? { ...data.items[0], sections } : { ...data, sections };

          this.debug('added', { items: data.items, sectionKeys: Object.keys(sections || {}) });

          const renderCart = () => {
            try {
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              cart.renderContents(state);
              this.debug('drawer rendered', { hasItems: !!cart.querySelector('.cart-item') });
            } catch (error) {
              console.error('mix-match-builder: cart render failed', error);
            }
            // Safety net: the theme opens the drawer from a timeout inside renderContents.
            setTimeout(() => {
              if (typeof cart.open === 'function' && !cart.classList.contains('active')) {
                this.debug('drawer was not opened by the theme — opening it');
                cart.open(this.el.add);
              }
            }, 100);
            // Notify other listeners only after the drawer is rendered, so their own refresh can't race it.
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mix-match-builder' });
            }
          };

          if (modal && modal.hasAttribute('open')) modal.hide();
          setTimeout(renderCart);
        } catch (error) {
          console.error('mix-match-builder: add to cart failed', error);
          this.showError('Could not add this pack to the cart. Please try again.');
        } finally {
          this.el.add.classList.remove('is-loading');
          this.el.add.removeAttribute('aria-busy');
        }
      }

      debug(...args) {
        if (this.debugEnabled === undefined) {
          this.debugEnabled = new URLSearchParams(window.location.search).has('mm_debug');
        }
        if (this.debugEnabled) console.info('[mix-match]', ...args);
      }

      async fetchSections(root, sectionIds) {
        try {
          const url = new URL(`${root}?sections=${sectionIds.join(',')}`, window.location.origin);
          url.searchParams.set('_mm', Date.now()); // bypass any cached cart render
          const response = await this.getCleanFetch()(url.toString(), { credentials: 'same-origin' });
          if (!response.ok) return null;
          return await response.json();
        } catch (error) {
          console.warn('mix-match-builder: could not refresh cart sections', error);
          return null;
        }
      }

      async cleanupMoonBundleAttributes(root) {
        try {
          const url = new URL(`${root}cart/update.js`, window.location.origin).toString();
          const request = this.getCleanFetch()(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({
              attributes: {
                moonbundle_cart_drawer_id: null,
                moonbundle_config_id: null,
                moonbundle_total_after_discounts: null,
                moonbundle_visitor_id: null,
              },
            }),
          });
          const timeout = new Promise((resolve) => setTimeout(() => resolve('timeout'), 1500));
          const outcome = await Promise.race([request, timeout]);
          if (outcome === 'timeout') {
            console.warn('mix-match-builder: moonbundle attribute cleanup timed out');
          } else {
            this.debug('moonbundle attribute cleanup ok', outcome.status);
          }
        } catch (error) {
          console.warn('mix-match-builder: moonbundle attribute cleanup failed', error);
        }
      }

      resetLine(lineKey) {
        this.state.counts[lineKey] = {};
        this.querySelectorAll(`[data-mm-grid="${lineKey}"] [data-mm-count]`).forEach((node) => (node.textContent = '0'));
        this.render();
      }
    }
  );
}
